import { useNavigate, useParams } from 'react-router-dom';
import {
  UPDATE_COHORT,
  CREATE_ASSESSMENT,
  CohortByIdQuery,
  CohortByIdQueryVariables,
  Enum_Assessment_Type,
  UpdateCohortMutation,
  UpdateCohortMutationVariables,
  FETCH_COHORT_BY_ID,
  CreateAssessmentWithStaffMutation,
  CreateAssessmentWithStaffMutationVariables,
} from '@edwin-group/cj-graphql';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { useToast } from '@/components/ui/use-toast';
import { useAuthentication } from '@/hooks/useAuthentication';
import { AssessmentStatus, useChapterStore } from '../../shared/store/useChapterStore';
import { GetCurrentSchoolYearUseCase } from '../../use-cases/get-current-school-year';
import { useAssessmentStore } from '../store/useAssessmentStore';
import { useMissionStore } from '../../shared/store/useMissionStore';
import { useRoot } from './useRoot';
import { useMissionRouteParams } from '../../shared/hooks/useMissionRouteParams';
import { trackGAEvent } from '@/lib/ga-custom-events/dispatcher';

export const useAssessmentPrePost = () => {
  useRoot();
  const { chapterTitleEncoded } = useMissionRouteParams();
  const mission = useMissionStore((state) => state.mission);
  const assessmentPage = useAssessmentStore((state) => state.assessmentPage);
  const status = useChapterStore((state) => state.status);

  const { cohortDocumentId } = useParams<{ cohortDocumentId: string }>();
  const navigate = useNavigate();
  const { data: cohortData, error: errorCohort } = useSuspenseQuery<CohortByIdQuery, CohortByIdQueryVariables>(
    FETCH_COHORT_BY_ID,
    {
      variables: { documentId: cohortDocumentId! },
      fetchPolicy: 'cache-and-network',
    },
  );
  const { schoolContext } = useAuthentication();
  const [updateCohort, { loading: loadingSubmit }] = useMutation<UpdateCohortMutation, UpdateCohortMutationVariables>(
    UPDATE_COHORT,
    {
      refetchQueries: 'all',
    },
  );

  const [createAssessment, { loading: loadingCreateAssessment }] = useMutation<
    CreateAssessmentWithStaffMutation,
    CreateAssessmentWithStaffMutationVariables
  >(CREATE_ASSESSMENT, {
    refetchQueries: 'all',
  });

  const { toast } = useToast();

  const onUpdateCohort = async () => {
    if (!cohortDocumentId) return;
    navigate(`/mission/${mission?.slug}/chapter/${chapterTitleEncoded}/cohort/${cohortDocumentId}/edit`);
  };

  const title =
    status === AssessmentStatus.PRE_ASSESSMENT
      ? assessmentPage?.beforeAndAfterSection?.beforeTitle
      : assessmentPage?.beforeAndAfterSection?.afterTitle;

  const description =
    status === AssessmentStatus.PRE_ASSESSMENT
      ? assessmentPage?.beforeAndAfterSection?.beforeDescription
      : assessmentPage?.beforeAndAfterSection?.afterDescription;

  const onSubmit = ({ answer, notes }: { answer: number; notes: string }) => {
    const assessment = {
      assessment: answer,
      notes,
      schoolYear: GetCurrentSchoolYearUseCase.execute(),
      missionStarted: mission?.missionStartedDocumentId,
      type: status === AssessmentStatus.PRE_ASSESSMENT ? Enum_Assessment_Type.Pre : Enum_Assessment_Type.Post,
      cohort: cohortData?.cohort?.documentId,
      school: schoolContext?.documentId,
    };
    createAssessment({
      variables: {
        data: assessment,
      },
    })
      .then(() => {
        toast({
          title: assessmentPage?.feedback?.assessmentCreatedSuccessTitle,
          description: assessmentPage?.feedback?.assessmentCreatedMessage,
          variant: 'default',
        });
        const eventName =
          status === AssessmentStatus.PRE_ASSESSMENT ? 'assessment_pre_completed' : 'assessment_post_completed';
        trackGAEvent(eventName, {
          mission_name: mission?.roleModel ?? '',
          mission_document_id: mission?.documentId ?? '',
          cohort_name: cohortData?.cohort?.name ?? '',
          cohort_document_id: cohortData?.cohort?.documentId ?? '',
        });
        if (status === AssessmentStatus.PRE_ASSESSMENT) {
          navigate(-1);
        } else {
          navigate(`/mission/${mission?.slug}/chapter/${chapterTitleEncoded}/assessment/${cohortDocumentId}/done`);
        }
      })
      .catch((error) => {
        toast({
          title: 'Failed to create assessment',
          description: error.message,
          variant: 'destructive',
        });
      });
  };

  const onPressDeleteCohort = () => {
    updateCohort({
      variables: {
        documentId: cohortDocumentId!,
        data: {
          isDeleted: true,
        },
      },
    })
      .then(() => {
        trackGAEvent('cohort_deleted', {
          cohort_name: cohortData?.cohort?.name ?? '',
          cohort_document_id: cohortDocumentId ?? '',
          mission_name: mission?.roleModel ?? '',
          mission_document_id: mission?.documentId ?? '',
        });
        navigate(-1);
      })
      .catch((error) => {
        toast({
          title: 'Failed to delete cohort',
          description: error.message,
          variant: 'destructive',
        });
      });
  };

  const onDeleteCohort = () => {
    if (window.confirm(assessmentPage?.feedback?.deleteCohortMessage)) {
      onPressDeleteCohort();
    }
  };

  return {
    onUpdateCohort,
    onDeleteCohort,
    onSubmit,
    status,
    mission,
    loadingSubmit,
    title,
    description,
    cohortData,
    errorCohort,
    loadingCreateAssessment,
  };
};
